import { useEffect, useRef } from 'react'
import { useIsMobile } from '@/hooks/use-mobile'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { isStartedTranslationRun, selectJobByCourse } from '@/features/ai/jobsSlice'
import { useGetStudioJobQuery } from '@/features/ai/studioApi'
import { pendingGateOf } from '@/features/ai/studioTypes'
import { setPanelOpen } from '@/features/editor/editorSlice'

/**
 * Панель открывается сама, когда у курса ПОЯВЛЯЕТСЯ запись в реестре прогонов
 * (C-1). Дефолт `aiSidebarOpen: false` при этом не трогаем — свёрнутая панель
 * это parity с рефом, где генерации не было вовсе.
 *
 * Условие именно «есть запись», а не «прогон идёт»: терминальная запись обязана
 * показать итог, иначе её никто не отцепит (`detach` зовёт только `RunOutcome`,
 * а он смонтирован внутри панели) и она навсегда останется в localStorage,
 * таща лишний `GET /ai/jobs/{id}` на каждой загрузке приложения.
 *
 * Покрывает оба входа разом: автор пришёл с главной, оплатив генерацию
 * (`attachJob` случился до перехода в редактор — на маунте запись уже есть), и
 * автор вернулся в редактор к прогону, начатому раньше.
 *
 * Открываем РОВНО ОДИН РАЗ на КАЖДЫЙ прогон, а не на каждый рендер: автор,
 * свернувший панель руками во время прогона, не должен получать её обратно
 * рывком каждые три секунды на новом тике опроса — но НОВЫЙ прогон обязан
 * показаться, даже если панель свёрнута под прошлый.
 *
 * Только от `md` и шире (B3): панель шириной 380 на экране 390 закрывает собой
 * хедер, канвас и таймлайн, а свернуть её нечем — `CollapsedSidebarTrigger` на
 * мобиле не рисуется вовсе. На узком экране запись остаётся непоказанной, зато
 * редактор цел; развернёт панель автор сам, когда экран станет шире.
 *
 * Important 2 (финальное ревью): пауза на вопросе — свой повод раскрыть
 * панель, а не тик прогона. Без второго ключа автор, свернувший панель во
 * время прогона, узнавал бы о встрявшем на вопросе прогоне только по тому,
 * что вернулся в редактор сам: жнец закрывает неотвеченный гейт через сутки
 * `gate_expired`, а нарисованные пилоты к этому моменту уже оплачены
 * (`GateCard.tsx`). Ключ — `gate.id`, тем же приёмом `useRef`, что и
 * `openedFor` выше: раскрытие на КАЖДЫЙ новый вопрос один раз, повторное
 * сворачивание автором во время того же вопроса уважается. Джобу читаем
 * СВОЕЙ подпиской на `getStudioJob`, а не через `useCourseJob().pendingGate`
 * — этому хуку не нужен ни прогресс, ни `entry`, только id гейта, и заводить
 * пропс ради одного числа было бы лишней связностью. Вторая подписка на тот
 * же `jobId` безопасна и не дублирует сеть: RTK Query делит кеш с
 * `JobPoller`/`useCourseJob`, второго запроса не будет — см. также
 * `useCourseBusyGate.ts` про ДРУГОЙ, по-настоящему избыточный случай.
 */
export const useAutoOpenForJob = (courseId: number): void => {
  const dispatch = useAppDispatch()
  const isMobile = useIsMobile()
  const jobId = useAppSelector((s) => selectJobByCourse(s, courseId)?.jobId ?? null)
  // Перевод живёт в меню шапки: ради него панель не раскрываем. Вопрос прогона — раскрываем.
  const isTranslation = useAppSelector((s) =>
    isStartedTranslationRun(selectJobByCourse(s, courseId)),
  )
  const { data } = useGetStudioJobQuery(jobId ?? 0, { skip: jobId === null })
  // Гейт на `jobId`, а не `data` напрямую (M-6, финальное ревью, тот же приём,
  // что `useCourseJob.ts:47-52`): RTK Query держит последний успешный `data`
  // даже когда `skip` становится `true` — без гейта `gateId` мог бы на кадр
  // указать на вопрос ПРОШЛОЙ джобы записи, которую только что заменили.
  const job = jobId === null ? null : (data ?? null)
  const gateId = pendingGateOf(job)?.id ?? null
  // Ключ — jobId ЗАПИСИ, а не courseId: запись реестра заменяема, и старт
  // отрисовки поверх незакрытого итога генерации (`attachJob` на том же курсе)
  // не создаёт запись, а подменяет её. Отметка по курсу в этом случае навсегда
  // гасила бы раскрытие, и платный прогон шёл бы в панели нулевой ширины —
  // без прогресса, ленты, итога и списка пропусков.
  const openedFor = useRef<number | null>(null)
  const openedForGate = useRef<number | null>(null)

  useEffect(() => {
    if (jobId === null) {
      openedFor.current = null
      return
    }
    if (isTranslation || openedFor.current === jobId) return
    // Отметку НЕ ставим: экран может стать шире (поворот, resize), и тогда
    // прогон обязан показаться — эффект перезапустится по `isMobile`.
    if (isMobile) return
    openedFor.current = jobId
    dispatch(setPanelOpen({ panel: 'aiSidebarOpen', open: true }))
  }, [jobId, isTranslation, isMobile, dispatch])

  useEffect(() => {
    if (gateId === null) {
      openedForGate.current = null
      return
    }
    if (openedForGate.current === gateId) return
    if (isMobile) return
    openedForGate.current = gateId
    dispatch(setPanelOpen({ panel: 'aiSidebarOpen', open: true }))
  }, [gateId, isMobile, dispatch])
}
